import { z } from 'zod';
import { Email, Id, IsoDate, PageQuerySchema, SearchQuery, Slug } from './common';
import { DomainStatus, PlatformRole, SubscriptionStatus, TenantStatus } from './enums';
import { SubscriptionAssignSchema, SubscriptionSchema } from './plans';
import { BrandingSchema, TenantDomainSchema } from './tenants';
import { MemberSchema } from './users';
/* ============================================================================
   Mission HQ (platform console)
   ========================================================================== */
export const HqOverviewSchema = z.object({
    kpis: z.object({
        activeTenants: z.number().int(),
        giftedTenants: z.number().int(),
        expiringIn14Days: z.number().int(),
        openInquiries: z.number().int(),
        seatsUsed: z.number().int(),
        missionsTracked: z.number().int(),
    }),
    expiringSoon: z.array(z.object({
        tenantId: Id,
        tenantName: z.string(),
        planName: z.string(),
        endsAt: IsoDate,
        daysLeft: z.number().int(),
    })),
    recentAudit: z.array(z.object({ id: Id, actor: z.string(), action: z.string(), tenantName: z.string().nullable(), createdAt: IsoDate })),
});
export const HqTenantRowSchema = z.object({
    id: Id,
    code: z.string(), // T-0042
    name: z.string(),
    slug: z.string(),
    logoUrl: z.string().nullable(),
    status: z.enum(TenantStatus),
    subscriptionStatus: z.enum(SubscriptionStatus),
    planName: z.string(),
    gifted: z.boolean(),
    seats: z.object({ used: z.number().int(), limit: z.number().int() }),
    endsAt: IsoDate,
    owner: z.object({ name: z.string(), email: z.string() }).nullable(),
    domain: z.object({ hostname: z.string(), status: z.enum(DomainStatus) }).nullable(),
    createdAt: IsoDate,
});
export const HqTenantListQuery = PageQuerySchema.merge(SearchQuery).extend({
    status: z.enum([...TenantStatus, 'gifted', 'grace', 'read_only', 'expiring']).optional(),
    planId: Id.optional(),
    sort: z.enum(['name', '-createdAt', 'endsAt', 'seats']).default('-createdAt'),
});
export const HqTenantDetailSchema = HqTenantRowSchema.extend({
    temporaryUrl: z.string().url(),
    branding: BrandingSchema,
    domains: z.array(TenantDomainSchema),
    subscription: SubscriptionSchema,
    subscriptionHistory: z.array(SubscriptionSchema),
    usage: z.object({
        users: z.number().int(),
        workspaces: z.number().int(),
        projects: z.number().int(),
        missions: z.number().int(),
        storageMb: z.number(),
    }),
    workspaces: z.array(z.object({ id: Id, name: z.string() })),
    pendingDeletion: z.object({ scheduledFor: IsoDate, requestedBy: z.string() }).nullable(),
});
/** Onboard a tenant from Mission HQ → returns a copyable invite link. */
export const HqTenantCreateSchema = z.object({
    companyName: z.string().trim().min(2).max(80),
    slug: Slug,
    ownerName: z.string().trim().min(2).max(80),
    ownerEmail: Email,
    clientDomain: z.string().max(253).optional(),
    subscription: SubscriptionAssignSchema,
    sendEmail: z.boolean().default(false),
});
export const HqTenantCreateResultSchema = z.object({
    tenantId: Id,
    inviteUrl: z.string().url(),
    whatsappUrl: z.string().url(),
});
export const HqTenantActionSchema = z.object({ reason: z.string().trim().min(3).max(500) });
export const HqTenantDeleteSchema = z.object({ confirmName: z.string(), reason: z.string().trim().min(3).max(500) });
export const HqEnterTenantResultSchema = z.object({ url: z.string().url(), expiresAt: IsoDate });
export const HqTenantUsersSchema = z.object({ data: z.array(MemberSchema) });
/* ---------- Platform team ---------- */
export const PlatformUserSchema = z.object({
    id: Id,
    name: z.string(),
    email: z.string(),
    role: z.enum(PlatformRole),
    totpEnabled: z.boolean(),
    status: z.enum(['active', 'invited', 'disabled']),
    lastLoginAt: IsoDate.nullable(),
    createdAt: IsoDate,
});
export const PlatformUserInviteSchema = z.object({
    name: z.string().trim().min(2).max(80),
    email: Email,
    role: z.enum(PlatformRole),
});
export const PlatformUserInviteResultSchema = z.object({ user: PlatformUserSchema, setupUrl: z.string().url() });
export const PlatformUserUpdateSchema = z.object({
    role: z.enum(PlatformRole).optional(),
    status: z.enum(['active', 'disabled']).optional(),
});
/* ---------- Broadcast announcements ---------- */
export const BroadcastSchema = z.object({
    id: Id,
    title: z.string(),
    body: z.string(),
    audience: z.object({
        kind: z.enum(['all', 'plan', 'tenants']),
        planIds: z.array(Id).default([]),
        tenantIds: z.array(Id).default([]),
        roles: z.enum(['owners', 'admins', 'everyone']),
    }),
    channels: z.object({ inApp: z.literal(true), push: z.boolean(), email: z.boolean() }),
    scheduledFor: IsoDate.nullable(),
    sentAt: IsoDate.nullable(),
    recipientCount: z.number().int().nullable(),
    createdBy: z.string(),
    createdAt: IsoDate,
});
export const BroadcastCreateSchema = BroadcastSchema.pick({
    title: true,
    body: true,
    audience: true,
    channels: true,
    scheduledFor: true,
});
/* ---------- Free tier status (System card) ---------- */
export const FreeTierStatusSchema = z.object({
    keepWarm: z.object({ lastTickAt: IsoDate.nullable(), source: z.literal('github-actions') }),
    database: z.object({ createdAt: IsoDate, expiresAt: IsoDate, daysLeft: z.number().int() }),
    lastBackup: z.object({ at: IsoDate, sizeBytes: z.number().int(), objectKey: z.string() }).nullable(),
});
//# sourceMappingURL=admin.js.map